<template>
    <div class="alert-silence-form">
        <LaBaseForm
            :form-data="formData"
            :vuex-action-mutation="formAction"
            :button="isEdit ? 'Save' : 'Create silence'"
            id="alert-silence"
            buttonPortalTo="modalFooter"
        >
            <InputContainer label="Alert type" name="alertCode">
                <div class="base-input-container">
                    <LaBaseSelect
                        data-test="silence-code"
                        :items="codeItems"
                        v-model="alertCode"
                        :stretch="true"
                        :searchable="false"
                    />
                </div>
            </InputContainer>

            <LaBaseTextarea
                v-model="matchers"
                name="matchers"
                label="Matches (JSON)"
                placeholder='{"publisher_id": 12}'
                data-test="silence-matchers"
                :rows="3"
                :rules="rules.matchers"
            />

            <p class="hint">
                Empty mutes every alert of the type. Otherwise every key must equal the alert's value, a list means any
                of them:
                <code>{"cron_id": ["daily_data_ltba", "daily_data_yield_approved"]}</code>.
                <template v-if="variables.length">
                    Keys of this type:
                    <code v-for="variable in variables" :key="variable" class="variable">{{ variable }}</code>
                </template>
            </p>

            <LaBaseInput
                v-model="reason"
                name="reason"
                label="Reason"
                placeholder="Why are these alerts muted?"
                data-test="silence-reason"
                :rules="rules.reason"
            />

            <InputContainer label="Expires" name="expires">
                <div class="base-input-container">
                    <LaBaseSelect
                        data-test="silence-expires"
                        :items="expiryItems"
                        v-model="expiry"
                        :stretch="true"
                        :searchable="false"
                    />
                </div>
            </InputContainer>
        </LaBaseForm>
    </div>
</template>

<script>
import LaBaseForm from "@/components/BaseForm";
import LaBaseInput from "@/components/inputs/BaseInput";
import LaBaseTextarea from "@/components/inputs/BaseTextarea";
import LaBaseSelect from "@/components/inputs/BaseSelect";
import InputContainer from "@/components/inputs/InputContainer";
import { computed, ref, watch } from "vue";
import store from "@/store/store";
import { isJsonObject, isRequired } from "@/helpers/rules";
import { formatDateTimePragueTimeZone } from "@/helpers/filters.js";
import { getterTypes as dialogGetterTypes } from "@/store/Dialog/types";
import { actionTypes as alertActionTypes, getterTypes as alertGetterTypes } from "@/store/Alerts/types";

const ALL_TYPES = "all types";
const HOUR_MS = 3600 * 1000;
const EXPIRY_OPTIONS = {
    "1 hour": HOUR_MS,
    "1 day": 24 * HOUR_MS,
    "7 days": 7 * 24 * HOUR_MS,
    "30 days": 30 * 24 * HOUR_MS,
    never: null,
};

export default {
    name: "LaAlertSilenceForm",
    components: {
        LaBaseForm,
        LaBaseInput,
        LaBaseTextarea,
        LaBaseSelect,
        InputContainer,
    },
    setup() {
        const silence = computed(() => store.getters[dialogGetterTypes.getDialog].item);
        const configs = computed(() => store.getters[alertGetterTypes.getConfigs] || []);
        const isEdit = !!silence.value;

        // Editing keeps the current expiry unless another option is picked
        const keepCurrent = silence.value
            ? `keep: ${silence.value.expires_at ? formatDateTimePragueTimeZone(silence.value.expires_at) : "never"}`
            : null;

        const codeItems = computed(() => [ALL_TYPES, ...configs.value.map((config) => config.code)]);
        const expiryItems = [...(keepCurrent ? [keepCurrent] : []), ...Object.keys(EXPIRY_OPTIONS)];

        const alertCode = ref(silence.value?.alert_code || ALL_TYPES);
        const matchers = ref(silence.value?.matchers ? JSON.stringify(silence.value.matchers) : "");
        const reason = ref(silence.value?.reason || "");
        const expiry = ref(keepCurrent || "1 day");

        const expiresAt = () => {
            if (expiry.value === keepCurrent) {
                return silence.value.expires_at ? `${silence.value.expires_at}Z` : null;
            }
            const duration = EXPIRY_OPTIONS[expiry.value];
            return duration === null ? null : new Date(Date.now() + duration).toISOString();
        };

        const parsedMatchers = () => {
            try {
                return matchers.value.trim() ? JSON.parse(matchers.value) : null;
            } catch (e) {
                return null; // the rule keeps the form from being submitted
            }
        };

        const formData = computed(() => ({
            id: silence.value?.id,
            alert_code: alertCode.value === ALL_TYPES ? null : alertCode.value,
            matchers: parsedMatchers(),
            reason: reason.value,
            // evaluated at submit time, relative to now
            get expires_at() {
                return expiresAt();
            },
        }));

        // The data keys the selected type's alerts carry - what can be matched on
        const variables = ref([]);
        const loadVariables = () => {
            const config = configs.value.find((item) => item.code === alertCode.value);
            if (!config) {
                variables.value = [];
                return;
            }
            store
                .dispatch(alertActionTypes.preview, {
                    code: config.code,
                    title_template: config.title_template,
                    item_template: config.item_template,
                })
                .then((data) => (variables.value = data.variables || []))
                .catch(() => (variables.value = []));
        };
        watch(alertCode, loadVariables, { immediate: true });

        const rules = {
            matchers: [isJsonObject()],
            reason: [isRequired()],
        };

        return {
            isEdit,
            codeItems,
            expiryItems,
            alertCode,
            matchers,
            reason,
            expiry,
            formData,
            formAction: alertActionTypes.saveSilence,
            variables,
            rules,
        };
    },
};
</script>

<style lang="scss" scoped>
.hint {
    font-size: 0.85rem;
    opacity: 0.8;
    margin-bottom: 0.8rem;
}
.variable {
    margin-right: 0.3rem;
}
</style>
